import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Seo } from "../components/Seo";
import { PageHeader, Reveal, ClipImage } from "../components/Motion";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/content";

const STEPS = [
  { t: "Identify", d: "We hear about available inventory early, through brands, factories and suppliers we have worked with for years." },
  { t: "Evaluate", d: "We review the assortment, quantities, condition and origin, and decide which retail channels it suits." },
  { t: "Coordinate", d: "We agree terms with both sides and organize freight, documentation and any preparation the retailer requires." },
  { t: "Deliver", d: "Goods move to the buyer's distribution centers on the agreed schedule, and we follow the program through to completion." },
];

const ServiceRow = ({ s, i }) => (
  <article id={s.id} className="scroll-mt-32 grid gap-4 md:gap-8 border-t border-[var(--line)] py-10 lg:py-14 md:grid-cols-12" data-testid={`service-${s.id}`}>
    <Reveal className="md:col-span-1">
      <span className="block pt-2 text-xs tabular-nums tracking-[0.12em] text-[var(--ink-soft)]">0{i + 1}</span>
    </Reveal>
    <Reveal className="md:col-span-5 lg:col-span-4" delay={0.05}>
      <h2 className="font-serif-display !font-normal text-2xl lg:text-[1.75rem] leading-[1.2] max-w-[18ch]">{s.title}</h2>
    </Reveal>
    <Reveal className="md:col-span-6 lg:col-span-6 lg:col-start-7" delay={0.1}>
      <p className="body-copy max-w-[34rem]">{s.text}</p>
    </Reveal>
  </article>
);

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <Seo
        title="Services — Sourcing, Closeouts & Distribution | 1818 Brands"
        description="Global merchandise sourcing, branded closeouts, excess production inventory, international distribution, off-price retail supply and logistics coordination."
      />
      <PageHeader
        eyebrow="Services"
        title="What we do, and how we do it."
        intro="We find branded inventory, arrange the transaction, and deliver it to off-price retailers. Each service below is one part of that work."
        testId="services-header"
      />
      <section className="container-x section" data-testid="services-list">
        <div className="border-b border-[var(--line)] [&>article:first-child]:border-t-[var(--ink)]">
        {SERVICES.map((s, i) => (
          <ServiceRow key={s.id} s={s} i={i} />
        ))}
        </div>
      </section>
      <section className="bg-[var(--paper-2)]/60 section" data-testid="process-section">
        <div className="container-x">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="eyebrow mb-6 text-[var(--ink-soft)]">How a program comes together</p>
                <h2 className="font-serif-display text-[2rem] sm:text-4xl lg:text-[2.75rem] leading-[1.1] max-w-[16ch]">Identify the opportunity. Execute the transaction.</h2>
              </Reveal>
              <ClipImage src="/img/racks.jpg" alt="Racks of branded apparel" className="mt-12 aspect-[4/3] w-full" testId="process-image" />
            </div>
            <ol className="lg:col-span-6 lg:col-start-7 lg:pt-2">
              {STEPS.map((s, i) => (
                <Reveal key={s.t} as="li" delay={i * 0.06} className="relative border-l border-[var(--line)] pl-8 sm:pl-10 pb-12 last:pb-0">
                  <span className="absolute -left-[4px] top-1.5 h-[7px] w-[7px] rounded-full bg-[var(--ink)]" />
                  <p className="eyebrow text-[var(--ink-soft)]">Step 0{i + 1}</p>
                  <h3 className="mt-3 font-serif-display !font-normal text-2xl">{s.t}</h3>
                  <p className="mt-3 max-w-[30rem] body-copy">{s.d}</p>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
